import { pathOr } from "ramda";

import { theme } from "@sisuwellness/web-components";
import {
    isMobileView,
    createLegends,
    calculateMinMax,
    createYGridValues,
    flattenPointValues,
    createPlottableDataPoints,
    calculateMeanValue
} from "components/Templates/TrendChart/Line/utils";
import { METRIC_INDICATOR } from "utilities/metrics/mapIndicators";
import calculatePointColor from "utilities/trend-chart/calculateGuldeline";
import { ALCOHOL_RISK } from "constants/trend-charts";
import i18n from "../../../../i18n.js";

const defaultColor = theme.portalColor.hpPrimaryPurple.hex;

const METRIC_FIELDS = {
    rounded: true,
    key: ["alcoholScore"],
    additionalKeys: ["rating", "unit"]
};

/**
 * Update data with rating
 * @param {object} data
 * @return {{ rating: { key: string, label: string, ranges: object } }}
 */
const alcoholRiskRatingCalc = data => {
    const { guideline, currentValue } = calculatePointColor(ALCOHOL_RISK, data);

    return {
        ...data,
        alcoholScore: Number(currentValue),
        rating: { key: guideline?.key, label: guideline?.label, ranges: guideline?.ranges }
    };
};

const plot = {
    unit: () => i18n.t("words.alcohol"),

    color: ({ rating } = {}) => theme.guidelines.Alcohol[(rating?.key)]?.sisuPortal?.hex ?? defaultColor,

    dataPoints: (healthChecks = [], settings) => {
        const points = createPlottableDataPoints(data => alcoholRiskRatingCalc(data, settings))(
            healthChecks,
            METRIC_FIELDS.key,
            METRIC_FIELDS.additionalKeys
        );

        return points;
    },

    yRange: (points = []) => {
        const { maximum } = calculateMinMax(flattenPointValues(points, "y"));
        const ranges = createYGridValues(0, maximum, { parts: 5, rounded: METRIC_FIELDS.rounded });

        return {
            min: 0,
            yScaleRanges: ranges,
            max: ranges[ranges.length - 1]
        };
    },

    legends: (points = []) =>
        createLegends(pathOr({}, [0, "data", 0, "rating", "ranges"], points), METRIC_INDICATOR.ALCOHOL_RISK.themeKey),

    historyProps: (filteredPoints = [], latestHealthCheckData = {}) => {
        const { alcoholScore } = latestHealthCheckData;
        const { rating } = alcoholRiskRatingCalc({ alcoholScore });

        const meanAlcoholScore = calculateMeanValue("alcoholScore", filteredPoints[0]);

        const { rating: meanRating } = alcoholRiskRatingCalc({ alcoholScore: meanAlcoholScore });

        return {
            value: alcoholScore,
            meanValue: Math.round(meanAlcoholScore),
            valueColor: theme.guidelines.Alcohol[(rating?.key)]?.sisuPortal?.hex,
            meanColor: theme.guidelines.Alcohol[(meanRating?.key)]?.sisuPortal?.hex
        };
    },

    margins: width => ({ right: isMobileView(width) ? 16 : 80, left: isMobileView(width) ? 50 : 80 })
};

export default { plottingUtility: plot };
